'use client';

import { Button } from '@pickleballinc/react-ui';
import { useState } from 'react';
import { toast } from 'react-toastify';

import { UnfollowConfirmModal } from '@/modules/players/v3/components/shared/following-modal/unfollow-confirm-modal';
import { useGetRelationship } from '@/modules/players/v3/hooks/use-get-relationship';
import { useSendFriendRequest } from '@/modules/players/v3/hooks/use-send-friend-request';
import { useUnfollow } from '@/modules/players/v3/hooks/use-unfollow';
import { FriendCardType } from '@/modules/players/v3/types/friend';

interface FollowActionButtonProps {
  playerUuid: string;
  userUuid: string;
}

export const FollowActionButton = ({ playerUuid, userUuid }: FollowActionButtonProps) => {
  const { data: relationship, isLoading } = useGetRelationship({
    userUuid,
    targetUuid: playerUuid,
  });

  const { mutate: sendFollowRequest, isPending: isSending } = useSendFriendRequest();
  const { mutate: unfollowMutate, isPending: isUnfollowing } = useUnfollow();
  const [showUnfollowConfirm, setShowUnfollowConfirm] = useState(false);

  if (isLoading) {
    return (
      <div
        className='!rounded !px-2 !py-1 !text-sm h-[28px] w-[60px] animate-pulse bg-gray-200'
        aria-hidden
      />
    );
  }

  const isFollowing = relationship?.isFollowing ?? false;

  // Follow is offered only from a clean slate — blocked or already-friends states own no follow control.
  if (
    relationship?.isBlocked ||
    (!isFollowing && relationship?.friendType === FriendCardType.FRIEND)
  ) {
    return null;
  }

  const handleFollow = () => {
    sendFollowRequest(
      { uuid: userUuid, friendUuid: playerUuid, intent: 'follow' },
      {
        onSuccess: (alreadyConnected) =>
          toast.success(
            alreadyConnected ? 'You are already connected with this player' : 'Player followed'
          ),
        onError: () => toast.error('Failed to follow player. Please try again.'),
      }
    );
  };

  const handleUnfollow = () => {
    setShowUnfollowConfirm(false);
    unfollowMutate(
      { uuid: userUuid, followedUuid: playerUuid },
      {
        onSuccess: () => toast.success('Player unfollowed'),
        onError: () => toast.error('Failed to unfollow player. Please try again.'),
      }
    );
  };

  return (
    <>
      {isFollowing ? (
        <Button
          variant='secondary'
          size='sm'
          className='!rounded !px-2 !py-1 !text-sm text-purple-600 hover:!text-purple-700 whitespace-nowrap'
          onClick={() => setShowUnfollowConfirm(true)}
          disabled={isUnfollowing}
        >
          {isUnfollowing ? 'Unfollowing...' : 'Unfollow'}
        </Button>
      ) : (
        <Button
          variant='primary'
          size='sm'
          className='!rounded !px-2 !py-1 !text-sm whitespace-nowrap bg-experiment'
          onClick={handleFollow}
          disabled={isSending}
        >
          {isSending ? 'Following...' : 'Follow'}
        </Button>
      )}

      <UnfollowConfirmModal
        open={showUnfollowConfirm}
        onOpenChange={setShowUnfollowConfirm}
        onConfirm={handleUnfollow}
      />
    </>
  );
};
